import { z } from "zod";
import { dailyLogFormSchema } from "./schema";

const userSchema = z.object({
  id: z.number(),
  name: z.string(),
  avatar_url: z.string().nullable(),
});
/** POST …/daily-logs 요청 본문 — `tags` 항목 */
export const dailyLogSubmitRequestTagSchema = z.object({
  id: z.number(),
  name: z.string(),
});

/** POST …/daily-logs 요청 본문 — `workforces` / `equipments` 항목 */
export const dailyLogSubmitRequestResourceSchema = z.object({
  contractor_id: z.number(),
  trade_id: z.number(),
  quantity: z.number(),
});

/** 공사일지 응답의 개별 인력/장비 항목 */
export const dailyLogResponseItemSchema = z.object({
  contractor_id: z.number(),
  contractor_name: z.string(),
  trade_id: z.number(),
  trade_name: z.string(),
  quantity: z.number(),
});

/** 공사일지 응답의 인력/장비 그룹 */
export const dailyLogResourceGroupSchema = z.object({
  total_quantity: z.number(),
  items: z.array(dailyLogResponseItemSchema),
});

/** 공사일지 최종 제출 요청 (서버 계약) */
export const dailyLogSubmitRequestSchema = z.object({
  draft_id: z.string(),
  description: z.string(),
  location: z.string(),
  date: z.string(),
  weather: z.string(),
  temperature: z.string(),
  temperature_unit: z.literal("F"),
  precipitation: z.string(),
  precipitation_unit: z.literal("MM"),
  process_rate: z.string(),
  tags: z.array(dailyLogSubmitRequestTagSchema),
  workforces: z.array(dailyLogSubmitRequestResourceSchema),
  equipments: z.array(dailyLogSubmitRequestResourceSchema),
  image_urls: z.array(z.string()),
});

export const dailyLogSubmitResponseImageSchema = z.object({
  id: z.number(),
  image_upload_path: z.string(),
  webp_image_upload_path: z.string().nullable(),
  webp_image_upload_path_resized: z.string().nullable(),
});

/** 공사일지 최종 제출 응답 (서버 계약) */
export const dailyLogSubmitResponseSchema = z.object({
  modified_at: z.string(),
  id: z.number(),
  status: z.string(),
  project_id: z.number(),
  description: z.string(),
  user: userSchema,
  location: z.string(),
  date: z.string(),
  d_day: z.string(),
  weather: z.string(),
  temperature: z.string(),
  temperature_unit: z.string(),
  precipitation: z.string(),
  precipitation_unit: z.string(),
  process_rate: z.string(),
  tags: z.array(dailyLogSubmitRequestTagSchema),
  workforce: dailyLogResourceGroupSchema.optional(),
  equipment: dailyLogResourceGroupSchema.optional(),
  images: z.array(dailyLogSubmitResponseImageSchema),
});

const dailyLogFormWithDraftSchema = z.object({
  draft_id: z.string(),
  ...dailyLogFormSchema.shape,
});

/**
 * 폼 값 + `draft_id` → 최종 제출 API 요청 본문.
 * `workforce_contractors` / `equipment_contractors` → `workforces` / `equipments` 플래튼 변환
 */
export const dailyLogFormWithDraftToSubmitRequestSchema = dailyLogFormWithDraftSchema.transform((data) =>
  dailyLogSubmitRequestSchema.parse({
    draft_id: data.draft_id,
    description: data.description,
    location: data.location,
    date: data.date,
    weather: data.weather,
    temperature: data.temperature.toString(),
    temperature_unit: "F" as const,
    precipitation: data.precipitation.toString(),
    precipitation_unit: "MM" as const,
    process_rate: data.processRate.toString(),
    tags: data.tags,
    workforces: data.workforce_contractors.flatMap((c) =>
      c.workforces.map((w) => ({ contractor_id: c.id, trade_id: Number(w.id), quantity: w.quantity })),
    ),
    equipments: data.equipment_contractors.flatMap((c) =>
      c.equipments.map((e) => ({ contractor_id: c.id, trade_id: Number(e.id), quantity: e.quantity })),
    ),
    image_urls: data.image_urls,
  }),
);

export type DailyLogResponseItem = z.infer<typeof dailyLogResponseItemSchema>;
export type DailyLogResourceGroup = z.infer<typeof dailyLogResourceGroupSchema>;
export type DailyLogSubmitRequest = z.infer<typeof dailyLogSubmitRequestSchema>;
export type DailyLogSubmitResponse = z.infer<typeof dailyLogSubmitResponseSchema>;
export type DailyLogSubmitResponseImage = z.infer<typeof dailyLogSubmitResponseImageSchema>;
/** `{ draft_id } & DailyLogFormValues` — `dailyLogFormWithDraftToSubmitRequestSchema` 입력 */
export type DailyLogFormWithDraftInput = z.input<typeof dailyLogFormWithDraftToSubmitRequestSchema>;
